<template>
    <div>
        <div v-if="loadingDetail" class="d-flex justify-center pa-8">
            <v-progress-circular indeterminate></v-progress-circular>
        </div>
        <v-form v-else v-model="valid" @submit.prevent="save" :disabled="saving">
            <v-overlay :model-value="saving && aiPrompt && !editEntity"
                       class="align-center justify-center text-center">
                <div class="pa-4 rounded bg-brown-lighten-5">
                    <h2 class="w-100 text-center ai-text ai-animated">AI Generating Questions... Please wait...</h2>
                </div>
            </v-overlay>
            <!-- Quiz Details -->
            <v-card variant="outlined" class="mb-6 mt-2">
                <v-card-title class="text-subtitle-1">Quiz Details</v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col cols="12" md="6">
                            <v-text-field v-model="form.name"
                                          label="Quiz Name *"
                                          :rules="[$rules.required]"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.number="form.totalTimeMinutes"
                                          label="Duration (minutes) *"
                                          type="number"
                                          min="1"
                                          :rules="[$rules.required]"></v-text-field>
                        </v-col>
                        <v-col cols="12">
                            <v-textarea v-model="form.welcomeMessage"
                                        label="Welcome Message"
                                        rows="3"
                                        hint="Shown to participants before they start the quiz"
                                        persistent-hint></v-textarea>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <!-- Questions (only when editing an existing quiz) -->
            <template v-if="editEntity">
                <div class="d-flex align-center mb-3">
                    <span class="text-h6">Questions</span>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" prepend-icon="mdi-plus" @click="addQuestion">
                        Add Question
                    </v-btn>
                </div>

                <div v-if="form.questions.length === 0" class="text-center pa-8 text-medium-emphasis">
                    <v-icon size="48" class="mb-2">mdi-help-circle-outline</v-icon>
                    <div>No questions yet. Add your first question.</div>
                </div>

                <v-card v-for="(question, qi) in form.questions"
                        :key="question._key"
                        variant="outlined"
                        class="mb-4">
                    <v-card-text>
                        <div class="d-flex align-center mb-3">
                            <span class="text-subtitle-2 text-medium-emphasis mr-3">Q{{ qi + 1 }}</span>
                            <v-text-field v-model="question.text"
                                          label="Question text *"
                                          hide-details
                                          density="compact"
                                          class="flex-grow-1"></v-text-field>
                            <div class="ml-2 d-flex">
                                <v-btn icon="mdi-arrow-up"
                                       size="small"
                                       variant="text"
                                       :disabled="qi === 0"
                                       @click="moveQuestion(qi, -1)"></v-btn>
                                <v-btn icon="mdi-arrow-down"
                                       size="small"
                                       variant="text"
                                       :disabled="qi === form.questions.length - 1"
                                       @click="moveQuestion(qi, 1)"></v-btn>
                                <v-btn icon="mdi-delete"
                                       size="small"
                                       variant="text"
                                       color="error"
                                       @click="removeQuestion(qi)"></v-btn>
                            </div>
                        </div>

                        <div class="ml-6">
                            <div v-for="(option, oi) in question.options"
                                 :key="option._key"
                                 class="d-flex align-center mb-2">
                                <v-checkbox v-model="option.isCorrect"
                                            color="success"
                                            density="compact"
                                            hide-details
                                            class="flex-shrink-0 mr-2">
                                    <template v-slot:label>
                                        <span class="text-caption text-medium-emphasis" style="white-space: nowrap">Correct</span>
                                    </template>
                                </v-checkbox>
                                <v-text-field v-model="option.text"
                                              :label="`Option ${oi + 1}`"
                                              hide-details
                                              density="compact"
                                              class="flex-grow-1"></v-text-field>
                                <v-btn icon="mdi-close"
                                       size="small"
                                       variant="text"
                                       color="error"
                                       class="ml-1"
                                       :disabled="question.options.length <= 2"
                                       @click="removeOption(question, oi)"></v-btn>
                            </div>

                            <v-btn size="small"
                                   variant="text"
                                   prepend-icon="mdi-plus"
                                   @click="addOption(question)"
                                   class="mt-1">
                                Add Option
                            </v-btn>

                            <v-alert v-if="!question.options.some(o => o.isCorrect)"
                                     type="warning"
                                     density="compact"
                                     variant="tonal"
                                     class="mt-2 text-caption">
                                Mark at least one option as correct
                            </v-alert>
                        </div>
                    </v-card-text>
                </v-card>

                <div v-if="form.questions.length > 0" class="mb-6">
                    <v-btn color="primary" variant="outlined" prepend-icon="mdi-plus" @click="addQuestion" block>
                        Add Another Question
                    </v-btn>
                </div>
            </template>

            <!-- AI Generate Questions (create only) -->
            <template v-if="!editEntity">

                <v-card variant="outlined" class="mb-4">
                    <v-card-title class="text-subtitle-1"><v-icon icon="mdi-brain" color="#d783ff"></v-icon> <span class="ai-text ai-animated">AI Generate Quiz Questions</span></v-card-title>
                    <v-card-text>
                        <v-alert type="info"
                                 variant="tonal"
                                 icon="mdi-help-circle-outline"
                                 class="mb-4"
                                 density="compact">
                            Enter a topic below and AI will automatically generate {{ Math.trunc((form.totalTimeMinutes * 60) / 45) }} multiple choice questions for you. Leave blank to add questions manually after creating the quiz.
                        </v-alert>
                        <v-textarea v-model="aiPrompt"
                                    label="Topic / Prompt"
                                    rows="3"
                                    hint="Describe the topic you want to quiz on (e.g. 'Workplace Health and Safety procedures')"
                                    persistent-hint
                                    clearable></v-textarea>

                        <div class="mt-4">
                            <div class="text-subtitle-2 mb-1">
                                Difficulty
                                <span v-if="aiPrompt" class="text-error ml-1">*</span>
                            </div>
                            <v-radio-group v-model="aiDifficulty"
                                           :rules="aiPrompt ? [$rules.required] : []"
                                           inline
                                           hide-details="auto">
                                <v-radio label="Easy (Basic Recall)" value="Easy"></v-radio>
                                <v-radio label="Medium (Understanding &amp; Application)" value="Medium"></v-radio>
                                <v-radio label="Hard (Analysis &amp; Problem-Solving)" value="Hard"></v-radio>
                            </v-radio-group>
                            <div v-if="aiPrompt && !aiDifficulty" class="text-error text-caption mt-1">
                                Difficulty is required when a prompt is provided
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </template>

            <v-alert v-if="errors" type="warning" class="my-2" :title="errors.title">
                <ul>
                    <li v-for="error in errors.errors" :key="error">{{ error[0] }}</li>
                </ul>
            </v-alert>

            <div class="mt-4 pt-2">
                <v-btn :loading="saving"
                       type="submit"
                       :disabled="!valid || (!!aiPrompt && !aiDifficulty)"
                       flat
                       color="primary"
                       prepend-icon="mdi-floppy">SAVE</v-btn>
                <v-btn flat class="ml-2" @click="showEntityForm = false" :disabled="saving">CANCEL</v-btn>
            </div>
        </v-form>
    </div>
</template>

<script>
let keyCounter = 0;
function makeKey() { return ++keyCounter; }

import { mapActions, mapState } from 'vuex';

export default {
    name: 'QuizEditDialog',
    data() {
        return {
            loadingDetail: false,
            saving: false,
            valid: false,
            errors: null,
            aiPrompt: '',
            aiDifficulty: null,
            form: {
                name: '',
                totalTimeMinutes: 15,
                welcomeMessage: '',
                questions: []
            }
        };
    },
    mounted() {
        if (this.editEntity) {
            this.form.name = this.editEntity.name ?? '';
            this.form.totalTimeMinutes = this.editEntity.totalTimeMinutes ?? 15;
            this.form.welcomeMessage = this.editEntity.welcomeMessage ?? '';
            this.loadDetail();
        }
    },
    methods: {
        ...mapActions(['showSuccessSnack', 'showErrorSnack', 'reloadEntities']),
        loadDetail() {
            this.loadingDetail = true;
            this.$api.get(`quiz/${this.editEntity.id}/detail`)
                .then(res => { this.form = this.mapFromApi(res.data); })
                .catch(() => this.showErrorSnack('Failed to load quiz details'))
                .finally(() => { this.loadingDetail = false; });
        },
        mapFromApi(data) {
            return {
                name: data.name ?? '',
                totalTimeMinutes: data.totalTimeMinutes ?? 30,
                welcomeMessage: data.welcomeMessage ?? '',
                questions: (data.questions ?? []).map(q => ({
                    _key: makeKey(),
                    id: q.id ?? 0,
                    text: q.name ?? '',
                    options: (q.options ?? []).map(o => ({
                        _key: makeKey(),
                        id: o.id ?? 0,
                        text: o.name ?? '',
                        isCorrect: o.isCorrect ?? false
                    }))
                }))
            };
        },
        addQuestion() {
            this.form.questions.push({
                _key: makeKey(),
                id: 0,
                text: '',
                options: [
                    { _key: makeKey(), id: 0, text: '', isCorrect: false },
                    { _key: makeKey(), id: 0, text: '', isCorrect: false },
                    { _key: makeKey(), id: 0, text: '', isCorrect: false },
                    { _key: makeKey(), id: 0, text: '', isCorrect: false }
                ]
            });
        },
        removeQuestion(index) {
            this.form.questions.splice(index, 1);
        },
        moveQuestion(index, direction) {
            const newIndex = index + direction;
            const qs = this.form.questions;
            const [item] = qs.splice(index, 1);
            qs.splice(newIndex, 0, item);
        },
        addOption(question) {
            question.options.push({ _key: makeKey(), id: 0, text: '', isCorrect: false });
        },
        removeOption(question, index) {
            question.options.splice(index, 1);
        },
        save() {
            this.saving = true;
            this.errors = null;

            if (this.editEntity) {
                // Edit: save full detail with questions
                const payload = {
                    name: this.form.name,
                    totalTimeMinutes: this.form.totalTimeMinutes,
                    welcomeMessage: this.form.welcomeMessage,
                    questions: this.form.questions.map(q => ({
                        id: q.id,
                        text: q.text,
                        options: q.options.map(o => ({
                            id: o.id,
                            text: o.text,
                            isCorrect: o.isCorrect
                        }))
                    }))
                };
                this.$api.put(`quiz/${this.editEntity.id}/detail`, payload)
                    .then(() => {
                        this.showEntityForm = false;
                        this.reloadEntities();
                        this.showSuccessSnack('Quiz saved!');
                    })
                    .catch(() => { this.errors = { title: 'Error', errors: [['Failed to save quiz.']] }; })
                    .finally(() => { this.saving = false; });
            } else {
                // Create: basic quiz info, with optional AI question generation
                this.$api.post('quiz/create', {
                    name: this.form.name,
                    totalTimeMinutes: this.form.totalTimeMinutes,
                    welcomeMessage: this.form.welcomeMessage,
                    aiPrompt: this.aiPrompt || null,
                    aiDifficulty: this.aiDifficulty || null
                })
                    .then(res => {
                        this.showEntityForm = false;
                        this.reloadEntities();
                        this.showSuccessSnack(this.aiPrompt ? 'Quiz created with AI questions!' : 'Quiz created!');
                        this.$router.push('/quiz/' + res.data.id);
                    })
                    .catch(() => { this.errors = { title: 'Error', errors: [['Failed to create quiz.']] }; })
                    .finally(() => { this.saving = false; });
            }
        }
    },
    computed: {
        ...mapState({
            entity: state => state.entities.entity,
            editEntity: state => state.entities.editEntity
        }),
        showEntityForm: {
            get() { return this.$store.state.entities.showEntityForm; },
            set(value) { this.$store.commit('setShowEntityForm', value); }
        }
    }
};
</script>
